"use client";

import { SiteFrame } from "@/components/layout/SiteFrame";
import { Link, useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { IconArrowLeft } from "@tabler/icons-react";
import { SectionNav } from "./SectionNav";
import { legalPageNavItems } from "@/utils/constants/layout";
import type { LegalDocumentSection } from "./types";
import { usePathname } from "@/i18n/navigation";
import { SectionContent } from "./SectionContent";

type LegalPageLayoutProps = {
  title: string;
  lastUpdated?: string;
  sections: LegalDocumentSection[];
};

export const LegalPageLayout = ({
  title,
  lastUpdated,
  sections,
}: LegalPageLayoutProps) => {
  const pathname = usePathname();
  const router = useRouter();

  return (
    <SiteFrame outerClassName="pb-12 pt-0 md:pb-16 md:pt-0">
      <div className="">
        <nav className="flex sticky md:top-35 top-18 items-center gap-4 border-b mb-4 py-4 text-base bg-white z-10">
          {legalPageNavItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "relative text-caption transition-colors hover:text-secondary",
                  isActive && "text-secondary",
                )}
              >
                {item.label}
                {isActive ? (
                  <span className="absolute inset-x-0 bottom-[-1px] h-[2px] rounded-full bg-seconadary" />
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="grid gap-8 lg:grid-cols-[323px_minmax(0,1fr)] lg:gap-12">
          <aside className="space-y-4 lg:sticky lg:top-55 lg:self-start lg:max-h-[calc(100vh-80px)] lg:overflow-y-auto lg:tovtrip-scrollbar">
            <button onClick={() => router.back()}>
              <div className="flex items-start gap-4">
                <IconArrowLeft
                  className="mt-1 size-5 shrink-0 text-[#1C1E21]"
                  stroke={2}
                />
                <div className="flex flex-col items-start gap-2">
                  <p className="whitespace-nowrap text-xl font-semibold text-heading">
                    {title}
                  </p>
                  {lastUpdated ? (
                    <p className="text-sm text-[#909498]">
                      Last updated: {lastUpdated}
                    </p>
                  ) : null}
                </div>
              </div>
            </button>
            <div className="hidden lg:block">
              <SectionNav sections={sections} />
            </div>
          </aside>

          <div className="space-y-10">
            {sections.map((section) => (
              <SectionContent key={section.id} section={section} />
            ))}

            <div className="border-t pt-6 text-base text-[#6D7580]">
              Have questions? Email us at{" "}
              <a
                href="mailto:support@tovtrip.com"
                className="font-semibold text-[#0B9BFF] hover:underline"
              >
                support@tovtrip.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </SiteFrame>
  );
};
